import PropTypes from 'prop-types';
import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Icon, LiveStatusRegion } from 'ui';
import { EVENT_NAMES } from '../../../constants/eventNames';
import { CONVERSATION_MODES } from '../../../constants/modes';
import { STREAM_STATUS } from '../../../constants/status';
import { useAIChatAgentConfig } from '../../../contexts/AIChatAgentConfig/useAIChatAgentConfig';
import { useAIChatConversation } from '../../../contexts/AIChatConversation/useAIChatConversation';
import { useAIChatShell } from '../../../contexts/AIChatShell/useAIChatShell';
import useAnalytics from '../../../../hooks/useAnalytics';
import useConversationEngine from '../../../hooks/useConversationEngine';
import useIsAIQuotaExhausted from '../../../hooks/useIsAIQuotaExhausted';
import useRecentConversations from '../../../hooks/useRecentConversations';
import useTutorAnnouncement from '../../../hooks/useTutorAnnouncement';
import ChatActionPills from '../../atoms/ChatActionPills/ChatActionPills';
import ChatDevTools from '../../atoms/ChatDevTools/ChatDevTools';
import ChatDropdownPortal from '../../atoms/ChatDropdownPortal/ChatDropdownPortal';
import ChatHeader from '../../atoms/ChatHeader/ChatHeader';
import ChatInput from '../../atoms/ChatInput/ChatInput';
import ChatMessages from '../../atoms/ChatMessages/ChatMessages';
import { QUOTA_EXHAUSTED_NOTICE_ID } from '../../atoms/ChatQuotaExhaustedNotice/ChatQuotaExhaustedNotice';
import ChatQuotaUsage from '../../atoms/ChatQuotaUsage/ChatQuotaUsage';
import ConversationHistory from '../../atoms/ConversationHistory/ConversationHistory';
import './Chatbot.scss';

const NOOP = () => {};

function Chatbot({
  className = '',
  onClose = NOOP,
  onCollapse,
  chatService = null,
  errorMessage = 'An error occurred. Please try again.',
  isVisible = true,
  getChatContextData,
  headerShowLeftContent = true,
}) {
  const { isDevMode } = useAIChatShell();
  const { systemPrompt, setSystemPrompt, model, setModel, temperature, setTemperature, maxTokens, setMaxTokens } = useAIChatAgentConfig();
  const { setCurrentConversationId, chatContext, currentConversationId, mode, queueMessage, isLoadingConversation, pills } = useAIChatConversation();
  const { trackEvent } = useAnalytics();
  const isQuotaExhausted = useIsAIQuotaExhausted();

  // History shows a fixed window of the most recent conversations
  const {
    conversations,
    isLoading: isLoadingConversations,
    error: conversationsError,
    addConversation,
    updateConversation,
  } = useRecentConversations({
    chatService,
    isVisible,
  });

  // Update conversation after stream completes
  const updateLatestConversation = useCallback(
    async (conversationId) => {
      if (!chatService) return;

      try {
        // Fetch just the most recent conversation
        const response = await chatService.getAllConversations({ limit: 1 });
        const latestConversations = response.conversations || [];

        if (latestConversations.length > 0 && latestConversations[0].id === conversationId) {
          const updatedConversation = latestConversations[0];

          // Use the updateConversation method from the hook
          updateConversation(conversationId, updatedConversation);
        }
      } catch {
        // Silently fail - the title will remain as placeholder
      }
    },
    [chatService, updateConversation]
  );

  // First, get the chat conversation hook to have startNewChat available
  const { messages, streamingMessage, activeToolEvents, isStreaming, streamStatus, startNewChat } = useConversationEngine({
    chatService,
    isVisible,
    getChatContextData,
    onConversationCreated: addConversation,
    onStreamComplete: updateLatestConversation,
  });

  const tutorAnnouncement = useTutorAnnouncement({
    activeToolEvents,
    conversationId: currentConversationId,
    isQuotaExhausted,
    isVisible,
    messages,
    mode,
    streamingMessage,
  });

  const { t } = useTranslation(['chatbot']);

  // Dropdown state management
  const [activeDropdown, setActiveDropdown] = useState(null);

  // Drag and drop state
  const [isDragging, setIsDragging] = useState(false);
  const [isInvalidDrag, setIsInvalidDrag] = useState(false);
  const [droppedImage, setDroppedImage] = useState(null);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const dragCounterRef = useRef(0);
  const contentRef = useRef(null);

  // Refs for button anchors (for portal positioning)
  const historyButtonRef = useRef(null);

  // Handle dropdown toggling
  const handleToggleHistory = useCallback(() => {
    setActiveDropdown((prev) => (prev === 'history' ? null : 'history'));
  }, []);

  const closeDropdown = useCallback(() => {
    setActiveDropdown(null);
  }, []);

  // Handle conversation selection
  const handleSelectConversation = useCallback(
    (conversationId) => {
      const isResumingAnotherConversation = conversationId !== currentConversationId;
      if (isResumingAnotherConversation) {
        const selectedConversation = conversations.find((conversation) => conversation.id === conversationId);
        trackEvent(EVENT_NAMES.CONVERSATION_RESUMED, {
          conversation_id: conversationId,
          mode,
          ...(selectedConversation?.title ? { title: selectedConversation.title } : {}),
        }).catch((error) => console.error('Analytics tracking failed:', error));
      }

      setCurrentConversationId(conversationId);
      closeDropdown();
    },
    [conversations, currentConversationId, mode, trackEvent, setCurrentConversationId, closeDropdown]
  );

  // Handle new chat
  const handleStartNewChat = useCallback(() => {
    trackEvent(EVENT_NAMES.NEW_CONVERSATION_CLICKED, { mode }).catch((error) => console.error('Analytics tracking failed:', error));
    startNewChat();
    closeDropdown();
  }, [trackEvent, mode, startNewChat, closeDropdown]);

  // Handle message from ChatInput by adding to queue
  const handleInputMessage = useCallback(
    (message, imageFile) => {
      queueMessage({
        message: message,
        imageFile: imageFile,
        options: {}, // Regular messages don't have special options
      });
    },
    [queueMessage]
  );

  // Valid image types for drag & drop
  const validImageTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/webp', 'image/heic', 'image/heif'];

  // Check if a DataTransferItem is a valid image file
  const isValidImageFile = (item) => {
    // If type is empty, browser doesn't expose it - allow (will validate on drop)
    if (!item.type) return true;
    return validImageTypes.includes(item.type);
  };

  // Check if drag event contains valid image files
  const hasValidImageFiles = (dataTransfer) => {
    if (!dataTransfer.items || dataTransfer.items.length === 0) return false;
    for (let i = 0; i < dataTransfer.items.length; i++) {
      const item = dataTransfer.items[i];
      if (item.kind === 'file' && isValidImageFile(item)) {
        return true;
      }
    }
    return false;
  };

  // Drag and drop handlers for the entire chat area
  // Without quota nothing can be attached, so dropping is ignored and its overlay never shows. The
  // events are still prevented so the browser does not open the dropped file instead.
  const handleDragEnter = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (isQuotaExhausted) return;
      dragCounterRef.current++;

      // Always show overlay, but track if files are valid
      if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
        const isValid = hasValidImageFiles(e.dataTransfer);
        setIsDragging(true);
        setIsInvalidDrag(!isValid);
      }
    },
    [isQuotaExhausted]
  );

  const handleDragLeave = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      if (isQuotaExhausted) return;
      dragCounterRef.current--;
      if (dragCounterRef.current === 0) {
        setIsDragging(false);
        setIsInvalidDrag(false);
      }
    },
    [isQuotaExhausted]
  );

  const handleDragOver = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();

      // Set cursor feedback based on file validity
      const isValid = !isQuotaExhausted && hasValidImageFiles(e.dataTransfer);
      e.dataTransfer.dropEffect = isValid ? 'copy' : 'none';

      // Only track mouse position if showing overlay
      if (contentRef.current && isDragging) {
        const rect = contentRef.current.getBoundingClientRect();
        setMousePosition({
          x: e.clientX - rect.left,
          y: e.clientY - rect.top,
        });
      }
    },
    [isDragging, isQuotaExhausted]
  );

  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);
      setIsInvalidDrag(false);
      dragCounterRef.current = 0;
      if (isQuotaExhausted) return;

      const files = e.dataTransfer.files;
      if (files && files.length > 0) {
        const file = files[0];
        // Validate file type (fallback for browsers that didn't expose type during drag)
        if (validImageTypes.includes(file.type)) {
          setDroppedImage(file);
        }
        // If invalid, silently ignore - overlay already showed error message
      }
    },
    [isQuotaExhausted]
  );

  const handleDroppedImageHandled = useCallback(() => {
    setDroppedImage(null);
  }, []);

  // Memoize content to prevent unnecessary re-renders of the entire component
  const chatContent = useMemo(
    () => (
      <div className="chatbot__content">
        <ChatMessages
          messages={messages}
          streamingMessage={streamingMessage}
          activeToolEvents={activeToolEvents}
          showDebugTools={isDevMode}
          mode={mode}
          isLoading={isLoadingConversation}
          isQuotaExhausted={isQuotaExhausted}
        />

        {/* Outside ChatMessages: a live region wrapping the transcript would read every streamed
            chunk, and the whole history when switching conversations. */}
        <LiveStatusRegion message={tutorAnnouncement} />

        <ChatActionPills pills={pills} isDisabled={isQuotaExhausted} />

        <div className="chatbot__input-container">
          <ChatInput
            onSendMessage={handleInputMessage}
            disabled={isQuotaExhausted || isLoadingConversation || (isStreaming && streamStatus !== STREAM_STATUS.ERROR)}
            disabledReasonId={isQuotaExhausted ? QUOTA_EXHAUSTED_NOTICE_ID : null}
            droppedImage={droppedImage}
            mode={mode}
            onDroppedImageHandled={handleDroppedImageHandled}
          />
          <div className="chatbot__footer">
            <p className="chatbot__disclaimer">{t('chatbot:ai_disclaimer')}</p>
            <ChatQuotaUsage isTeacher={mode === CONVERSATION_MODES.TEACHER_TOOLS} />
          </div>
        </div>
      </div>
    ),
    [
      messages,
      streamingMessage,
      activeToolEvents,
      isDevMode,
      mode,
      isStreaming,
      handleInputMessage,
      streamStatus,
      errorMessage,
      droppedImage,
      handleDroppedImageHandled,
      isLoadingConversation,
      pills,
      t,
      tutorAnnouncement,
      isQuotaExhausted,
    ]
  );

  // Memoize dev tools to prevent unnecessary re-renders
  const devToolsComponent = useMemo(
    () =>
      isVisible && isDevMode ? (
        <ChatDevTools
          chatContext={chatContext}
          currentConversationId={currentConversationId}
          systemPrompt={systemPrompt}
          setSystemPrompt={setSystemPrompt}
          model={model}
          setModel={setModel}
          temperature={temperature}
          setTemperature={setTemperature}
          maxTokens={maxTokens}
          setMaxTokens={setMaxTokens}
          onNewChat={startNewChat}
        />
      ) : null,
    [
      isVisible,
      isDevMode,
      chatContext,
      currentConversationId,
      systemPrompt,
      setSystemPrompt,
      model,
      setModel,
      temperature,
      setTemperature,
      maxTokens,
      setMaxTokens,
      mode, // Add mode to dependencies since DevTools now uses it
      startNewChat, // Add startNewChat to dependencies
    ]
  );

  return (
    <div
      ref={contentRef}
      className={`chatbot ${className || ''}`}
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={handleDrop}
    >
      {isDragging && (
        <div className={`chatbot__drag-overlay ${isInvalidDrag ? 'chatbot__drag-overlay--invalid' : ''}`}>
          <span
            className={`chatbot__drag-overlay-text ${isInvalidDrag ? 'chatbot__drag-overlay-text--invalid' : ''}`}
            style={{ left: mousePosition.x, top: mousePosition.y }}
          >
            {isInvalidDrag ? (
              <>
                <Icon name="warning" size="xs" color="red" />
                {t('This file is not an image')}
              </>
            ) : (
              t('Drag and drop images here')
            )}
          </span>
        </div>
      )}
      {isVisible && (
        <>
          <div className="chatbot__header-wrapper">
            <ChatHeader
              onClose={onClose}
              onCollapse={onCollapse}
              onNewChat={handleStartNewChat}
              isNewChatDisabled={isQuotaExhausted}
              onToggleHistory={handleToggleHistory}
              showHistory={activeDropdown === 'history'}
              showSettings={activeDropdown === 'settings'}
              historyButtonRef={historyButtonRef}
              showLeftContent={headerShowLeftContent}
            />
          </div>

          {/* Dropdowns rendered via Portal at document.body level */}
          <ChatDropdownPortal
            isOpen={activeDropdown === 'history'}
            onClose={closeDropdown}
            anchorRef={historyButtonRef}
            className="conversation-history-portal"
          >
            <ConversationHistory
              conversations={conversations}
              onNewChat={handleStartNewChat}
              isNewChatDisabled={isQuotaExhausted}
              onSelectConversation={handleSelectConversation}
              isLoading={isLoadingConversations}
              error={conversationsError}
            />
          </ChatDropdownPortal>

          {/* Eliminamos las opciones de settings ya que la opción de Habilitar búsqueda web queda relegada a feature flag en el backend
          <ChatDropdownPortal isOpen={activeDropdown === 'settings'} onClose={closeDropdown} anchorRef={settingsButtonRef} className="chat-settings-portal">
            <ChatSettingsPopup />
          </ChatDropdownPortal> */}
          {chatContent}
          {devToolsComponent}
        </>
      )}
    </div>
  );
}

Chatbot.propTypes = {
  className: PropTypes.string,
  onClose: PropTypes.func,
  onCollapse: PropTypes.func,
  chatService: PropTypes.object,
  errorMessage: PropTypes.string,
  isVisible: PropTypes.bool,
  getChatContextData: PropTypes.func.isRequired,
  headerShowLeftContent: PropTypes.bool,
};

export default Chatbot;
